import axios from "axios";
import { globalNavigate } from "./../utils/NavigationService";
import SnackbarUtils from "./../utils/SnackbarUtils";
import { triggerLogout } from "./../context/AuthHelper";
import PlatformBridge from "./PlatformBridge";

// Los logs de request/response (headers, body de cada llamada) solo tienen sentido
// en desarrollo — en producción quedarían corriendo siempre en la consola de
// cualquiera con acceso a la pantalla.
const isDev = process.env.NODE_ENV !== "production";
const devLog = (...args) => {
  if (isDev) console.log(...args);
};

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || "http://localhost:8080",
  timeout: 60000,
  headers: {
    "Content-Type": "application/json",
  },
});

let isNetworkSnackbarOpen = false;
let isRefreshing = false;
let refreshSubscribers = [];

const onRefreshed = (token) => {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
};

const addRefreshSubscriber = (callback) => {
  refreshSubscribers.push(callback);
};

const redirectTo = (path, clearTokens = false) => {
  if (clearTokens) {
    triggerLogout(); // 👈 esto dispara el logout real
  }

  try {
    globalNavigate(path);
  } catch {
    window.location.href = path;
  }
};

axiosInstance.interceptors.request.use(
  async (config) => {
    const token = await PlatformBridge.getToken("accessToken");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const language = localStorage.getItem("language") || "es";
    config.headers["Accept-Language"] = language;
    
    // Agregar X-Tenant-Id header desde localStorage
    // Solo agregar si existe empresaId para evitar problemas con preflight requests
    const empresaId = localStorage.getItem("empresaId");
    if (empresaId && empresaId !== "undefined" && empresaId !== "null") {
      config.headers["X-Tenant-Id"] = empresaId;
    }
    
    // Log detallado de la request
    devLog("📤 [AxiosConfig] Request:", {
      method: config.method?.toUpperCase(),
      url: config.url,
      baseURL: config.baseURL,
      fullURL: `${config.baseURL}${config.url}`,
      headers: {
        ...config.headers,
        Authorization: config.headers.Authorization ? "Bearer ***" : undefined
      },
      data: config.data,
      params: config.params
    });
    
    return config;
  },
  (error) => {
    console.error("❌ [AxiosConfig] Request Error:", error);
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response) => {
    // Log detallado de la response exitosa
    devLog("📥 [AxiosConfig] Response Success:", {
      status: response.status,
      statusText: response.statusText,
      url: response.config.url,
      method: response.config.method?.toUpperCase(),
      headers: response.headers,
      data: response.data
    });
    return response;
  },
  async (error) => {
    const { config, response } = error;
    const originalRequest = config;

    // 🚫 IMPORTANTE: evitar intento de refresh si el error viene de /session/login
    if (originalRequest?.url?.includes('/session/login') && response?.status === 401) {
      devLog("🔁 Login failed with 401 — not retrying token refresh");
      return Promise.reject(error);
    }
    
    // Log detallado del error
    console.error("❌ [AxiosConfig] Response Error:", {
      url: originalRequest?.url,
      fullURL: `${originalRequest?.baseURL}${originalRequest?.url}`,
      method: originalRequest?.method?.toUpperCase(),
      status: response?.status,
      statusText: response?.statusText,
      headers: response?.headers,
      data: response?.data,
      errorMessage: error.message,
      errorCode: error.code,
      _retry: originalRequest?._retry,
      requestHeaders: originalRequest?.headers
    });

    // 🧱 No respuesta del servidor
    if (!response) {
      // Verificar si es un error de CORS o de red
      const isCorsError = error.message && (
        error.message.includes('CORS') || 
        error.message.includes('Network Error') ||
        error.code === 'ERR_NETWORK'
      );
      
      if (!isNetworkSnackbarOpen) {
        isNetworkSnackbarOpen = true;
        const errorMessage = "Sin conexión con el servidor. Verificá tu conexión a internet.";
        
        SnackbarUtils.error(errorMessage, {
          onClose: () => { isNetworkSnackbarOpen = false; },
          autoHideDuration: 6000,
        });
      }
      return Promise.reject(error);
    }

    // Primer 401 → intento de refresh
    if (response.status === 401 && !originalRequest._retry) {
      devLog("🔐 First 401: attempting token refresh");

      // Asegurarse de marcar la request como ya reintentada (no enumerable, no clonable)
      Object.defineProperty(originalRequest, "_retry", {
        value: true,
        writable: true,
        configurable: true,
      });

      if (isRefreshing) {
        devLog("⏳ Waiting for ongoing refresh to complete");
        return new Promise((resolve) => {
          addRefreshSubscriber((token) => {
            devLog("🔄 Retrying original request with new token");
            // Crear una nueva request sin la marca _retry para evitar problemas
            const retryRequest = { ...originalRequest };
            retryRequest.headers = { ...originalRequest.headers };
            retryRequest.headers.Authorization = `Bearer ${token}`;
            // Remover la marca _retry para permitir un nuevo intento si es necesario
            delete retryRequest._retry;
            resolve(axiosInstance(retryRequest));
          });
        });
      }

      isRefreshing = true;

      const storedAccessToken = await PlatformBridge.getToken("accessToken");
      const storedRefreshToken = await PlatformBridge.getToken("refreshToken");
      const storedEmpresaId = localStorage.getItem("empresaId");

      // Necesitamos accessToken, refreshToken y empresaId
      if (storedAccessToken) {
        try {
          if (!storedRefreshToken || storedRefreshToken === "undefined" || !storedEmpresaId) {
            throw new Error("Missing refreshToken or empresaId");
          }
          const empresaId = parseInt(storedEmpresaId);
          if (isNaN(empresaId)) {
            throw new Error("Invalid empresaId");
          }
          const refreshPayload = {
            accessToken: storedAccessToken,
            refreshToken: storedRefreshToken,
            empresaId: empresaId
          };

          devLog("Refresh payload:", refreshPayload);
          const refreshResponse = await axios.post(
            `${process.env.REACT_APP_API_BASE_URL}/session/refresh_token`,
            refreshPayload
          );

          const newAccessToken = refreshResponse.data.data;
          devLog("✅ Token refresh successful");

          await PlatformBridge.setToken("accessToken", newAccessToken);
          axiosInstance.defaults.headers.common["Authorization"] = `Bearer ${newAccessToken}`;
          originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;

          isRefreshing = false;
          onRefreshed(newAccessToken);
          // Notificar al WebSocket (y al AuthContext) para que reconecten con el nuevo token
          window.dispatchEvent(new CustomEvent("tokenRefreshed", { detail: newAccessToken }));

          devLog("🔁 Retrying original request after refresh");
          // Crear una nueva request sin la marca _retry para evitar problemas
          const retryRequest = { ...originalRequest };
          retryRequest.headers = { ...originalRequest.headers };
          retryRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          // Remover la marca _retry para permitir un nuevo intento si es necesario
          delete retryRequest._retry;
          return axiosInstance(retryRequest);
        } catch (err) {
          devLog("❌ Token refresh failed, redirecting to login");
          isRefreshing = false;
          redirectTo("/login", true);
          return Promise.reject(err);
        }
      } else {
        devLog("⚠️ AccessToken not found, redirecting to login");
        redirectTo("/login", true);
        return Promise.reject(error);
      }
    }

    if (response.status === 401 && originalRequest._retry) {
      devLog("🛑 Second 401 after refresh attempt — forcing logout");
      redirectTo("/login", true);
      return Promise.reject(error);
    }

    if (response.status === 403) {
      const url = originalRequest?.url ?? '';
      if (url.includes('/session/login')) {
        devLog("🚫 403 en login — dejando que el componente maneje el error");
        return Promise.reject(error);
      }
      if (originalRequest?.skipGlobalErrorRedirect) {
        return Promise.reject(error);
      }
      devLog("🚫 403 Forbidden — redirecting");
      redirectTo("/permission-denied");
    }

    // 🚫 No redirigir globalmente en 404 de API.
    // Cada pantalla/servicio decide si ese 404 es esperado (ej: listas vacías, recursos opcionales, etc.).
    if (response.status === 404) {
      const url = originalRequest?.url ?? '';
      if (url.includes('/session/login')) {
        devLog("🔍 404 en login — dejando que el componente maneje el error");
        return Promise.reject(error);
      }
      if (url.includes('/products/by_sku')) {
        devLog("🔍 404 en by_sku — producto no encontrado, el componente muestra el mensaje");
        return Promise.reject(error);
      }
      devLog("🔍 404 en API — sin redirección global");
      return Promise.reject(error);
    }

    if (response.status === 500 && !originalRequest?.skipGlobalErrorRedirect) {
      devLog("💥 500 Internal Server Error — redirecting");
      redirectTo("/internal-server-error");
    }

    if (response.status === 400 && response.data?.error_message) {
      devLog("⚠️ 400 Bad Request with message:", response.data.error_message);
      
      // No mostrar snackbar si es un error de diferencia de precio (el modal lo manejará)
      const errorMessage = response.data.error_message;
      const isPriceDifferenceError = errorMessage.includes('Algunos precios tienen una diferencia mayor al 10%') || 
                                     errorMessage.includes('Some prices have a difference greater than 10%');
      
      if (!isPriceDifferenceError && !originalRequest?.skipSnackbar) {
        // Extraer solo el mensaje sin el metadata JSON si existe
        const messageWithoutMetadata = errorMessage.split(':')[0].trim();
        SnackbarUtils.error(messageWithoutMetadata);
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
